import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Firebase } from '../../firebase/config';
import AdminLayout from '../../Components/Admin/AdminLayout';
import { fetchErc20Metadata, isValidEvmAddress } from '../../utils/erc20';
import { DEFAULT_TOKEN_CONFIG } from '../../utils/tokenConfig';

const NETWORK_OPTIONS = [
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'bsc', label: 'BNB Smart Chain' },
  { value: 'arbitrum', label: 'Arbitrum' },
  { value: 'optimism', label: 'Optimism' },
];

function TokenSection({ prefix, config, onChange, t }) {
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [fetched, setFetched] = useState(false);

  const address = config[`${prefix}Address`] || '';
  const network = config[`${prefix}Network`] || 'ethereum';
  const isReward = prefix === 'rewardToken';
  const eurField = isReward ? 'rewardEurPerToken' : 'eurPerToken';

  const handleField = (field, value) => {
    onChange(`${prefix}${field}`, value);
  };

  const handleFetch = () => {
    if (!isValidEvmAddress(address)) {
      setFetchError(t('admin.tokenInvalidAddress'));
      return;
    }
    setFetching(true);
    setFetchError('');
    setFetched(false);
    fetchErc20Metadata(address, network)
      .then((meta) => {
        onChange(`${prefix}Name`, meta.symbol);
        onChange(`${prefix}FullName`, meta.name);
        onChange(`${prefix}Decimals`, meta.decimals);
        setFetched(true);
      })
      .catch((err) => {
        console.error('Token metadata fetch failed:', err);
        setFetchError(t('admin.tokenFetchFailed'));
      })
      .finally(() => setFetching(false));
  };

  return (
    <section className="adminTokenSection">
      <h2>{isReward ? t('admin.rewardTokenSection') : t('admin.standardTokenSection')}</h2>
      <label htmlFor={`${prefix}-addr`}>{t('admin.tokenAddress')}</label>
      <div className="adminTokenAddressRow">
        <input
          id={`${prefix}-addr`}
          type="text"
          value={address}
          onChange={(e) => handleField('Address', e.target.value.trim())}
          placeholder="0x..."
        />
        <button
          type="button"
          className="adminTokenFetchBtn"
          onClick={handleFetch}
          disabled={fetching}
        >
          {fetching ? t('admin.tokenFetching') : t('admin.tokenFetchFromContract')}
        </button>
      </div>
      <label htmlFor={`${prefix}-net`}>{t('admin.tokenNetwork')}</label>
      <select
        id={`${prefix}-net`}
        value={network}
        onChange={(e) => handleField('Network', e.target.value)}
      >
        {NETWORK_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {/* Name, symbol and decimals come from the token contract itself */}
      <label htmlFor={`${prefix}-name`}>{t('admin.tokenSymbol')}</label>
      <input
        id={`${prefix}-name`}
        type="text"
        value={config[`${prefix}Name`] || ''}
        readOnly
        placeholder="—"
      />
      <label htmlFor={`${prefix}-fullname`}>{t('admin.tokenFullName')}</label>
      <input
        id={`${prefix}-fullname`}
        type="text"
        value={config[`${prefix}FullName`] || ''}
        readOnly
        placeholder="—"
      />
      <label htmlFor={`${prefix}-dec`}>{t('admin.tokenDecimals')}</label>
      <input
        id={`${prefix}-dec`}
        type="number"
        min="0"
        max="18"
        value={config[`${prefix}Decimals`] ?? ''}
        readOnly
        placeholder="—"
      />
      <label htmlFor={`${prefix}-eur`}>{t('admin.eurPerToken')}</label>
      <input
        id={`${prefix}-eur`}
        type="number"
        step="0.0001"
        min="0"
        value={config[eurField]}
        onChange={(e) => onChange(eurField, Number(e.target.value))}
      />
      {fetched && <p className="adminTokenFetched">{t('admin.tokenFetchedFromContract')}</p>}
      {fetchError && <p className="adminTokenFetchError">{fetchError}</p>}
    </section>
  );
}

function AdminTokenSettings() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(DEFAULT_TOKEN_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Firebase.firestore().doc('appSettings/tokenPayment')
      .get()
      .then((doc) => {
        if (doc.exists) setConfig({ ...DEFAULT_TOKEN_CONFIG, ...doc.data() });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    Firebase.firestore().doc('appSettings/tokenPayment')
      .set(config, { merge: true })
      .then(() => {
        setSaving(false);
        setSaved(true);
      })
      .catch(() => setSaving(false));
  };

  if (loading) {
    return (
      <AdminLayout>
        <h1>{t('admin.tokenSettings')}</h1>
        <p>{t('admin.loading')}</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1>{t('admin.tokenSettings')}</h1>
      <p className="adminHint">{t('admin.tokenSettingsDesc')}</p>
      <form onSubmit={handleSave} className="adminTokenForm">
        <TokenSection
          prefix="token"
          config={config}
          onChange={handleChange}
          t={t}
        />
        <TokenSection
          prefix="rewardToken"
          config={config}
          onChange={handleChange}
          t={t}
        />
        <label htmlFor="tk-comm">{t('admin.commissionPercent')}</label>
        <input
          id="tk-comm"
          type="number"
          step="0.1"
          min="0"
          max="100"
          value={config.commissionPercent}
          onChange={(e) => handleChange('commissionPercent', Number(e.target.value))}
        />
        <button type="submit" className="adminLangSaveBtn" disabled={saving}>
          {saving ? t('admin.loading') : t('common.save')}
        </button>
        {saved && <p className="adminTokenSaved">{t('admin.tokenSettingsSaved')}</p>}
      </form>
    </AdminLayout>
  );
}

export default AdminTokenSettings;
